import React from 'react';
import { X, ScrollText } from 'lucide-react';

// Terms of Use / EULA — presented BEFORE registration (App Review guideline
// 1.2: UGC apps must gate entry on terms that state zero tolerance for
// objectionable content and abusive users). The text is baked into the bundle
// on purpose: the reviewer must be able to reach it with no network hop, and
// the same content is hosted at /terms.html for the store listing.
// If you change the substance here, bump TERMS_VERSION and mirror the change
// in public/terms.html.
export const TERMS_VERSION = '2026-09-09';

const H = ({ children }) => (
  <h3 className="text-brand-teal font-black text-[11px] uppercase tracking-widest mt-5 mb-1.5">{children}</h3>
);

const TermsModal = ({ onAgree, onClose }) => (
  <div className="fixed inset-0 z-[9500] bg-black/90 flex flex-col animate-in fade-in">
    <div className="flex justify-between items-center px-5 pb-3 pt-[calc(env(safe-area-inset-top,0px)+1rem)] border-b border-zinc-800 bg-zinc-950">
      <div className="flex items-center gap-2">
        <ScrollText className="text-brand-teal" size={18} />
        <h2 className="text-white font-black italic uppercase tracking-tighter">Terms of Use</h2>
      </div>
      <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={24} /></button>
    </div>

    <div className="flex-1 overflow-y-auto px-5 py-4 text-[12px] leading-relaxed text-zinc-300">
      <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-3">Effective {TERMS_VERSION} · Bender Extender USA Inc.</p>

      <p>These Terms of Use are a binding agreement between you and Bender Extender USA Inc.
      ("we", "us") for the Bender Quest app. By creating an account or using the app you
      agree to these terms and to our <span className="text-brand-pink">Privacy Policy</span> (bendermap-69.web.app/privacy.html).</p>

      <H>Who can use Bender Quest</H>
      <p>You must be of legal drinking age in your location (and at least 17) to use this app.
      Bender Quest is a tracking companion — it never encourages drinking. Know your limits,
      drink water, and get home safe.</p>

      <H>Zero tolerance for objectionable content</H>
      <p>Bender Quest has <strong className="text-white">no tolerance for objectionable content or
      abusive users</strong>. In squad chat and anywhere else you can post content, you may not share
      anything that is harassing, hateful, threatening, discriminatory, sexually explicit, violent,
      illegal, or that targets or exploits minors, or that infringes anyone's rights or privacy
      (including photos of people who haven't consented).</p>

      <H>Moderation &amp; enforcement</H>
      <ul className="list-disc pl-5 space-y-1">
        <li>Messages pass through an automated objectionable-content filter.</li>
        <li>Every photo and message can be <strong className="text-white">reported</strong> from inside the app.</li>
        <li>Any user can be <strong className="text-white">blocked</strong> — their content disappears from your feed instantly and we are notified.</li>
        <li>We review reports and act <strong className="text-white">within 24 hours</strong>: offending content is removed and offending users are ejected from the app.</li>
      </ul>

      <H>Your content</H>
      <p>You own what you post. Squad photos are visible only to your squad and are deleted
      automatically after 24 hours. By posting, you confirm you have the right to share it and
      you grant us the limited license needed to display it inside the app and to review it for
      moderation.</p>

      <H>License &amp; acceptable use</H>
      <p>We grant you a personal, non-transferable license to use the app. Don't reverse-engineer
      it, abuse the service, spoof your identity or location, or interfere with other users.</p>

      <H>Termination</H>
      <p>We may suspend or permanently eject any account that violates these terms, without notice.
      You can stop using the app at any time; local data can be cleared from your device settings.</p>

      <H>Disclaimers</H>
      <p>The app is provided "as is". Location, step, and hydration figures are estimates, not
      medical or safety advice. To the fullest extent permitted by law we disclaim all warranties
      and are not liable for indirect or consequential damages arising from use of the app.</p>

      <H>Contact &amp; governing law</H>
      <p>Questions or content complaints: <span className="text-brand-pink">cody@hillforge.ai</span>.
      These terms are governed by the laws of British Columbia, Canada.</p>
    </div>

    {onAgree && (
      <div className="px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] border-t border-zinc-800 bg-zinc-950">
        <button
          onClick={onAgree}
          className="w-full bg-brand-teal text-black font-black py-4 rounded-2xl uppercase tracking-widest text-sm active:scale-95 transition-transform"
        >
          I Agree
        </button>
      </div>
    )}
  </div>
);

export default TermsModal;
